import {
  FormEvent,
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { isValidPhoneNumber } from "libphonenumber-js";
import {
  createUser,
  deleteUser,
  listUsers,
  updateUser,
} from "../../auth/shared/services/authService";
import {
  AuthUser,
  CreateUserPayload,
  USER_CATEGORY_VALUES,
  UpdateUserPayload,
} from "../../auth/shared/types/auth";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  formatCpf,
  isValidCpf,
  normalizeCpf as normalizeUserCpf,
} from "../../../shared/utils/cpf";
import { normalizeSearchText } from "../../../shared/utils/searchText";

const EMPTY_USER: CreateUserPayload = {
  name: "",
  email: "",
  password: "1234",
  cpf: "",
  phone: "",
  sex: "",
  category: "",
  clubId: null,
  role: "user",
};

export function buildUserDraft(
  user: AuthUser,
): UpdateUserPayload & { password: string } {
  return {
    name: user.name,
    email: user.email,
    role: user.role,
    cpf: formatCpf(user.cpf || ""),
    phone: user.phone || "",
    sex: user.sex || "",
    category: user.category || "",
    clubId: user.clubId ?? null,
    avatarUrl: user.avatarUrl || "",
    password: "",
  };
}

type UseAdminUsersParams = {
  authUserId?: number | null;
  isAdmin: boolean;
  isOrganizer: boolean;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  token?: string | null;
};

export function useAdminUsers({
  authUserId,
  isAdmin,
  isOrganizer,
  showErrorToast,
  showSuccessToast,
  token,
}: UseAdminUsersParams) {
  const t = siteTexts.admin;
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userDrafts, setUserDrafts] = useState<
    Record<number, UpdateUserPayload & { password: string }>
  >({});
  const [newUser, setNewUser] = useState<CreateUserPayload>(EMPTY_USER);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!token) return;
    setUsersLoading(true);
    try {
      const data = await listUsers(token);
      setUsers(data);
      const nextDrafts: Record<
        number,
        UpdateUserPayload & { password: string }
      > = {};
      data.forEach((user) => {
        nextDrafts[user.id] = buildUserDraft(user);
      });
      setUserDrafts(nextDrafts);
    } catch (_err) {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [token]);

  const filteredUsers = useMemo(() => {
    const query = normalizeSearchText(userSearch);
    if (!query) return users;

    const digitsQuery = query.replace(/\D/g, "");
    return users.filter((user) => {
      const nameMatch = normalizeSearchText(user.name).includes(query);
      const cpfDigits = String(user.cpf || "").replace(/\D/g, "");
      const cpfMatch = digitsQuery ? cpfDigits.includes(digitsQuery) : false;
      const emailMatch = normalizeSearchText(user.email).includes(query);
      return nameMatch || cpfMatch || emailMatch;
    });
  }, [users, userSearch]);

  const onCreateUser = async (event: FormEvent) => {
    event.preventDefault();
    if (!token) return;
    const cpf = normalizeUserCpf(newUser.cpf);
    if (!isValidCpf(cpf)) {
      showErrorToast(t.validation.invalidCpf);
      return;
    }
    const phone = String(newUser.phone || "").trim();
    if (!phone || !isValidPhoneNumber(phone)) {
      showErrorToast(t.validation.invalidPhone);
      return;
    }
    const payload = {
      ...(isOrganizer ? { ...newUser, role: "user" } : newUser),
      cpf,
      phone,
    } as CreateUserPayload;
    try {
      await createUser(token, payload);
      setNewUser(EMPTY_USER);
      setShowCreateUserModal(false);
      showSuccessToast(t.toasts.userCreatedSuccess);
      if (isAdmin) {
        await loadUsers();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      const lower = message.toLowerCase();
      if (
        lower.includes("cpf") &&
        (lower.includes("uso") || lower.includes("cadastrado"))
      ) {
        showErrorToast(t.validation.duplicateCpf);
        return;
      }
      showErrorToast(message || t.toasts.userCreatedError);
    }
  };

  const onCancelCreateUser = () => {
    const confirmed = window.confirm(t.confirms.cancelCreateUser);
    if (!confirmed) return;
    setNewUser(isOrganizer ? { ...EMPTY_USER, role: "user" } : EMPTY_USER);
    setShowCreateUserModal(false);
  };

  const onDismissCreateUserModal = () => {
    setShowCreateUserModal(false);
  };

  const onSaveUser = async (id: number) => {
    if (!token) return false;
    const draft = userDrafts[id];
    const currentUser = users.find((user) => user.id === id);
    if (!draft || !currentUser) return false;

    const safeCpf = normalizeUserCpf(draft.cpf ?? currentUser.cpf ?? "");
    const currentCpf = normalizeUserCpf(currentUser.cpf ?? "");
    if (safeCpf && safeCpf !== currentCpf && !isValidCpf(safeCpf)) {
      showErrorToast(t.validation.invalidCpf);
      return false;
    }

    const isSelf = Number(authUserId || 0) === Number(id);
    const safeDraft: UpdateUserPayload & { password: string } = {
      name: String(draft.name ?? currentUser.name ?? "").trim(),
      email: String(draft.email ?? currentUser.email ?? "").trim(),
      role: isSelf
        ? currentUser.role
        : draft.role === "admin" ||
            draft.role === "organizer" ||
            draft.role === "user"
          ? draft.role
          : currentUser.role,
      cpf: safeCpf,
      phone: String(draft.phone ?? currentUser.phone ?? "").trim(),
      sex:
        draft.sex === "male" || draft.sex === "female" || draft.sex === "other"
          ? draft.sex
          : "",
      category: USER_CATEGORY_VALUES.includes(draft.category)
        ? draft.category
        : "",
      clubId:
        draft.clubId === null ||
        draft.clubId === undefined ||
        Number(draft.clubId) <= 0
          ? null
          : Math.floor(Number(draft.clubId)),
      avatarUrl: String(draft.avatarUrl ?? currentUser.avatarUrl ?? "").trim(),
      password: String(draft.password || ""),
    };
    if (safeDraft.phone && !isValidPhoneNumber(safeDraft.phone)) {
      showErrorToast(t.validation.invalidPhone);
      return false;
    }

    const payload: UpdateUserPayload = {
      name: safeDraft.name,
      email: safeDraft.email,
      role: safeDraft.role,
      cpf: safeDraft.cpf,
      phone: safeDraft.phone,
      sex: safeDraft.sex,
      category: safeDraft.category,
      clubId: safeDraft.clubId,
      password: safeDraft.password || undefined,
    };
    if (
      String(safeDraft.avatarUrl || "") !==
      String(currentUser.avatarUrl || "")
    ) {
      payload.avatarUrl = safeDraft.avatarUrl;
    }

    try {
      await updateUser(token, id, payload);
      await loadUsers();
      showSuccessToast(t.toasts.userUpdatedSuccess);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      const lower = message.toLowerCase();
      if (
        lower.includes("cpf") &&
        (lower.includes("uso") || lower.includes("cadastrado"))
      ) {
        showErrorToast(t.validation.duplicateCpf);
        return false;
      }
      showErrorToast(message || t.toasts.userUpdatedError);
      return false;
    }
  };

  const onDeleteUser = async (id: number) => {
    if (!token) return;
    const confirmed = window.confirm(t.confirms.removeUser);
    if (!confirmed) return;

    try {
      await deleteUser(token, id);
      showSuccessToast(t.toasts.userRemovedSuccess);
      await loadUsers();
    } catch (_err) {
      showErrorToast(t.toasts.userRemovedError);
    }
  };

  return {
    buildUserDraft,
    filteredUsers,
    loadUsers,
    newUser,
    onCancelCreateUser,
    onCreateUser,
    onDeleteUser,
    onDismissCreateUserModal,
    onSaveUser,
    setNewUser,
    setShowCreateUserModal,
    setUserDrafts: setUserDrafts as Dispatch<
      SetStateAction<Record<number, UpdateUserPayload & { password: string }>>
    >,
    setUserSearch,
    showCreateUserModal,
    userDrafts,
    userSearch,
    users,
    usersLoading,
  };
}
